import React from "react";
import { FiCheckCircle, FiCopy } from "react-icons/fi";
import { useSearchParams } from "react-router-dom";
// import LiveMapReleaseContent from '../../components/LiveMapReleaseContent'
import brand from "../../assets/brand.png";
import api from "../../services/api";
import {
  BrandCopy,
  BrandLink,
  BrandMark,
  Footer,
  FooterLink,
  Glow,
  HeaderAction,
  NavAnchor,
  NavLinks,
  Page,
  Shell,
  TopBar,
} from "../../components/LiveMapReleaseContent/styles";
import {
  ButtonRow,
  CheckoutCard,
  CheckoutCardBody,
  CheckoutForm,
  CheckoutGlow,
  CheckoutGrid,
  CodeBlock,
  CodeLabel,
  CodeValue,
  Description,
  EmailInput,
  ErrorMessage,
  Eyebrow,
  FieldGroup,
  FieldLabel,
  HeroCopy,
  HeroSection,
  InlineNote,
  ModalActions,
  ModalBackdrop,
  ModalCard,
  ModalText,
  ModalTitle,
  Panel,
  PanelEyebrow,
  PanelHeader,
  PanelText,
  PanelTitle,
  PaymentSection,
  PriceHint,
  PriceLabel,
  PriceRow,
  PriceValue,
  PrimaryButton,
  QrCard,
  SecondaryButton,
  StatusPill,
  SuccessIconWrap,
  SuccessState,
  SuccessText,
  SuccessTitle,
  Title,
} from "./styles";

type PremiumCheckoutResponse = {
  id: string;
  email: string;
  amountCents: number;
  premiumDays: number;
  status: "pending" | "paid" | "expired" | "cancelled";
  qrCode?: string;
  qrCodeBase64?: string;
  paymentExpiresAt?: string;
  paidAt?: string;
  activatedAt?: string;
  premiumExpiresAt?: string;
  isPremiumActive: boolean;
};

const PREMIUM_PRICE_CENTS = 1000;
const PAYMENT_STATUS_POLLING_MS = 2000;

function formatCurrencyBRL(value: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function getStatusTone(status?: PremiumCheckoutResponse["status"]) {
  if (status === "paid") {
    return "success";
  }

  if (status === "pending") {
    return "warning";
  }

  return "neutral";
}

function getStatusLabel(order: PremiumCheckoutResponse | null) {
  if (!order) {
    return "Pronto para gerar PIX";
  }

  if (order.status === "paid") {
    return "Conta premium ativa";
  }

  if (order.status === "pending") {
    return "Aguardando pagamento PIX";
  }

  if (order.status === "expired") {
    return "PIX expirado";
  }

  return "Pagamento cancelado";
}

function extractErrorMessage(error: any) {
  return (
    error?.response?.data?.message ||
    error?.message ||
    "Nao foi possivel iniciar o checkout agora."
  );
}

function ensureQrCodeSrc(value?: string) {
  if (!value) {
    return "";
  }

  if (value.startsWith("data:")) {
    return value;
  }

  return `data:image/png;base64,${value}`;
}

export default function LiveMap() {
  const [searchParams] = useSearchParams();
  const lockedEmail = searchParams.get("email")?.trim() || "";
  const [email, setEmail] = React.useState("");
  const [checkout, setCheckout] = React.useState<PremiumCheckoutResponse | null>(null);
  const [isCreating, setIsCreating] = React.useState(false);
  const [isChecking, setIsChecking] = React.useState(false);
  const [isCopied, setIsCopied] = React.useState(false);
  const [error, setError] = React.useState("");
  const [isSuccessModalOpen, setIsSuccessModalOpen] = React.useState(false);

  const isPremiumActiveState = React.useCallback((value: PremiumCheckoutResponse | null) => {
    return Boolean(value && (value.status === "paid" || value.isPremiumActive === true));
  }, []);

  const applyCheckoutUpdate = React.useCallback(
    (nextCheckout: PremiumCheckoutResponse) => {
      setCheckout((currentCheckout) => {
        const wasPremiumActive = isPremiumActiveState(currentCheckout);
        const isPremiumActive = isPremiumActiveState(nextCheckout);

        if (!wasPremiumActive && isPremiumActive) {
          setIsSuccessModalOpen(true);
        }

        return nextCheckout;
      });
    },
    [isPremiumActiveState]
  );

  React.useEffect(() => {
    if (!lockedEmail) {
      return;
    }

    setEmail(lockedEmail);
    setCheckout(null);
    setError("");
    setIsCopied(false);
    setIsSuccessModalOpen(false);
  }, [lockedEmail]);

  React.useEffect(() => {
    if (!checkout || checkout.status !== "pending") {
      return;
    }

    const intervalId = window.setInterval(async () => {
      try {
        const { data } = await api.get<PremiumCheckoutResponse>("/premium/pix/check", {
          params: {
            orderId: checkout.id,
          },
        });

        applyCheckoutUpdate(data);
      } catch (requestError) {
        console.log(requestError);
      }
    }, PAYMENT_STATUS_POLLING_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [applyCheckoutUpdate, checkout]);

  React.useEffect(() => {
    if (!isCopied) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsCopied(false);
    }, 1800);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isCopied]);

  const handleCreatePix = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isValidEmail(email)) {
      setError("Informe um email valido para continuar.");
      return;
    }

    setError("");
    setIsCreating(true);

    try {
      const { data } = await api.post<PremiumCheckoutResponse>("/premium/pix", {
        email: email.trim(),
      });

      applyCheckoutUpdate(data);
    } catch (requestError) {
      setError(extractErrorMessage(requestError));
    } finally {
      setIsCreating(false);
    }
  };

  const handleCheckStatus = async () => {
    if (!checkout) {
      return;
    }

    setError("");
    setIsChecking(true);

    try {
      const { data } = await api.get<PremiumCheckoutResponse>("/premium/pix/check", {
        params: {
          orderId: checkout.id,
        },
      });

      applyCheckoutUpdate(data);
    } catch (requestError) {
      setError(extractErrorMessage(requestError));
    } finally {
      setIsChecking(false);
    }
  };

  const handleCopyPix = async () => {
    if (!checkout?.qrCode) {
      return;
    }

    try {
      await navigator.clipboard.writeText(checkout.qrCode);
      setIsCopied(true);
    } catch (requestError) {
      setError("Nao foi possivel copiar o codigo PIX agora.");
    }
  };

//   if (email) {
//   return <div><LiveMapReleaseContent /></div>
// }

  const isPremiumActivated = checkout?.status === "paid" || checkout?.isPremiumActive === true;

  return (
    <Page>
      <Glow />
      <Shell>
        <TopBar>
          <BrandLink href="/">
            <BrandMark alt="OndeHJ" src={brand} />
            <BrandCopy>Ative sua conta premium do OndeHJ.</BrandCopy>
          </BrandLink>

          <NavLinks>
            <NavAnchor href="/">Inicio</NavAnchor>
            <NavAnchor href="/livemap">Premium</NavAnchor>
            <NavAnchor href="/support">Suporte</NavAnchor>
          </NavLinks>

          <HeaderAction href="/">Voltar ao site</HeaderAction>
        </TopBar>

        <HeroSection>
          <HeroCopy>
            <Eyebrow>OndeHJ Premium</Eyebrow>
            <Title>Ative o premium e libere todas as funcionalidades do OndeHJ.</Title>
            <Description>
              Assim que o pagamento for confirmado, o acesso premium e liberado automaticamente
              para a conta {email}.
            </Description>
          </HeroCopy>

          <CheckoutCard>
            <CheckoutGlow />
            <CheckoutCardBody>
              <PriceRow>
                <div>
                  <PriceLabel>Premium</PriceLabel>
                  <PriceValue>{formatCurrencyBRL(PREMIUM_PRICE_CENTS / 100)}</PriceValue>
                </div>
                <PriceHint>
                  Ativacao premium com pagamento via PIX e confirmacao automatica.
                </PriceHint>
              </PriceRow>
            </CheckoutCardBody>
          </CheckoutCard>
        </HeroSection>

        <CheckoutGrid>
          <Panel>
            <PanelHeader>
              <div>
                <PanelEyebrow>Checkout</PanelEyebrow>
                <PanelTitle>Ativar conta premium</PanelTitle>
              </div>
              <StatusPill $tone={getStatusTone(checkout?.status)}>
                {getStatusLabel(checkout)}
              </StatusPill>
            </PanelHeader>

            <PanelText>
              Confirme o email da sua conta e gere o PIX para ativar o premium.
            </PanelText>

            <CheckoutForm onSubmit={handleCreatePix}>
              <FieldGroup>
                <FieldLabel htmlFor="premium-email">Email da conta</FieldLabel>
                <EmailInput
                  id="premium-email"
                  type="email"
                  value={email}
                  placeholder="voce@exemplo.com"
                  readOnly={Boolean(lockedEmail)}
                  onChange={(event) => {
                    if (lockedEmail) {
                      return;
                    }

                    setEmail(event.target.value);
                    setCheckout((currentCheckout) =>
                      currentCheckout?.email === event.target.value.trim().toLowerCase()
                        ? currentCheckout
                        : null
                    );
                    if (error) {
                      setError("");
                    }
                    if (isCopied) {
                      setIsCopied(false);
                    }
                  }}
                  required
                />
                <InlineNote>
                  {lockedEmail
                    ? "Email recebido do app e bloqueado para evitar ativacao na conta errada."
                    : "Use o mesmo email cadastrado no OndeHJ."}
                </InlineNote>
              </FieldGroup>

              <FieldGroup>
                <FieldLabel>Valor</FieldLabel>
                <EmailInput readOnly value={formatCurrencyBRL(PREMIUM_PRICE_CENTS / 100)} />
              </FieldGroup>

              {error ? <ErrorMessage>{error}</ErrorMessage> : null}

              <ButtonRow>
                <PrimaryButton disabled={isCreating || isPremiumActivated} type="submit">
                  {isPremiumActivated
                    ? "Premium ativado"
                    : isCreating
                      ? "Gerando PIX..."
                      : `Gerar PIX de ${formatCurrencyBRL(PREMIUM_PRICE_CENTS / 100)}`}
                </PrimaryButton>

                {checkout && !isPremiumActivated ? (
                  <SecondaryButton disabled={isChecking} onClick={handleCheckStatus} type="button">
                    {isChecking ? "Consultando..." : "Atualizar status"}
                  </SecondaryButton>
                ) : null}
              </ButtonRow>
            </CheckoutForm>
          </Panel>

          <Panel>
            <PanelHeader>
              <div>
                <PanelEyebrow>{isPremiumActivated ? "Ativado" : "Pagamento"}</PanelEyebrow>
                <PanelTitle>
                  {isPremiumActivated ? "Conta premium ativa" : "Pague o PIX para ativar"}
                </PanelTitle>
              </div>
              <StatusPill $tone={getStatusTone(checkout?.status)}>
                {isPremiumActivated ? "Ativo" : checkout?.status === "pending" ? "Aguardando" : "Pronto"}
              </StatusPill>
            </PanelHeader>

            {isPremiumActivated ? (
              <SuccessState>
                <SuccessIconWrap>
                  <FiCheckCircle />
                </SuccessIconWrap>
                <SuccessTitle>Premium ativado com sucesso</SuccessTitle>
                <SuccessText>
                  Sua conta premium do OndeHJ ja foi liberada. Agora voce pode acessar
                  todas as funcionalidades disponiveis para usuarios premium.
                </SuccessText>
              </SuccessState>
            ) : checkout?.qrCodeBase64 ? (
              <>
                <PanelText>
                  Abra o app do seu banco, escaneie o QRCode ou copie o codigo PIX para
                  concluir a ativacao.
                </PanelText>

              <PaymentSection>
                <QrCard>
                  <img alt="QR Code PIX OndeHJ Premium" src={ensureQrCodeSrc(checkout.qrCodeBase64)} />
                </QrCard>

                {checkout.qrCode ? (
                  <CodeBlock>
                    <CodeLabel>Copia e cola PIX</CodeLabel>
                    <CodeValue>{checkout.qrCode}</CodeValue>

                    <ButtonRow>
                      <SecondaryButton onClick={handleCopyPix} type="button">
                        <FiCopy />
                        {isCopied ? "Copiado" : "Copiar codigo"}
                      </SecondaryButton>
                    </ButtonRow>
                  </CodeBlock>
                ) : null}
              </PaymentSection>
              </>
            ) : (
              <PanelText>Gere o PIX para continuar com a ativacao da conta premium.</PanelText>
            )}
          </Panel>
        </CheckoutGrid>

        <Footer>
          <span>Checkout premium do OndeHJ.</span>
          <FooterLink href="/support">Falar com o suporte</FooterLink>
        </Footer>
      </Shell>

      {isSuccessModalOpen ? (
        <ModalBackdrop
          aria-modal="true"
          role="dialog"
          onClick={() => {
            setIsSuccessModalOpen(false);
          }}
        >
          <ModalCard
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <SuccessIconWrap>
              <FiCheckCircle />
            </SuccessIconWrap>
            <ModalTitle>Premium ativado com sucesso</ModalTitle>
            <ModalText>
              Sua conta premium do OndeHJ ja foi liberada. Para refletir a ativacao no
              aplicativo imediatamente, deslogue e entre novamente na sua conta.
            </ModalText>
            <ModalActions>
              <PrimaryButton
                onClick={() => {
                  setIsSuccessModalOpen(false);
                }}
                type="button"
              >
                Entendi
              </PrimaryButton>
            </ModalActions>
          </ModalCard>
        </ModalBackdrop>
      ) : null}
    </Page>
  );
}
